import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";
import { employeeHasRegularDayOff } from "./ScheduleView";

function TrainingAssignments() {
	const [employees, setEmployees] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [benches, setBenches] = useState([]);
	const [assignments, setAssignments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [editingAssignment, setEditingAssignment] = useState(null);
	const [formData, setFormData] = useState({
		employeeId: "",
		departmentId: "",
		benchId: "",
		startDate: "",
		endDate: "",
		reason: "",
		trainingRole: "",
	});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [selectedIds, setSelectedIds] = useState([]);
	const [selectedDates, setSelectedDates] = useState([]);
	const [calendarMonth, setCalendarMonth] = useState(new Date());
	const [filterMonth, setFilterMonth] = useState(() => {
		const now = new Date();
		return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
	});

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		try {
			const [empRes, deptRes, assignRes] = await Promise.all([
				axios.get(`${API_URL}/employees`),
				axios.get(`${API_URL}/departments`),
				axios.get(`${API_URL}/training`),
			]);
			setEmployees(empRes.data);
			setDepartments(deptRes.data);
			setAssignments(assignRes.data);

			// Get all benches from departments
			const allBenches = deptRes.data.reduce((acc, dept) => {
				if (dept.benches) {
					return [
						...acc,
						...dept.benches.map((b) => ({
							...b,
							department_id: dept.id,
							department_name: dept.name,
						})),
					];
				}
				return acc;
			}, []);
			setBenches(allBenches);
		} catch (error) {
			setError("Failed to load data");
		} finally {
			setLoading(false);
		}
	};

	const openModal = (assignment = null) => {
		setSelectedDates([]);
		setCalendarMonth(new Date());
		if (assignment) {
			setEditingAssignment(assignment);
			setFormData({
				employeeId: assignment.employee_id,
				departmentId: assignment.department_id,
				benchId: assignment.bench_id || "",
				startDate: assignment.assignment_date,
				endDate: assignment.assignment_date,
				reason: assignment.reason || "",
				trainingRole: assignment.training_role || "",
			});
		} else {
			setEditingAssignment(null);
			setFormData(
				normalizeDepartment({
					employeeId: employees.length > 0 ? employees[0].id : "",
					departmentId:
						departments.length > 0 ? departments[0].id : "",
					benchId: "",
					startDate: "",
					endDate: "",
					reason: "",
					trainingRole: "",
				}),
			);
		}
		setShowModal(true);
		setError("");
	};

	const getCalendarDays = (date) => {
		const year = date.getFullYear();
		const month = date.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const days = Array(firstDay.getDay()).fill(null);

		for (let day = 1; day <= lastDay.getDate(); day++) {
			days.push(new Date(year, month, day));
		}
		return days;
	};

	const formatDateStr = (date) =>
		`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

	const toggleAssignmentDate = (dateStr) => {
		setSelectedDates((dates) =>
			dates.includes(dateStr)
				? dates.filter((date) => date !== dateStr)
				: [...dates, dateStr].sort(),
		);
	};

	const changeCalendarMonth = (monthDelta) => {
		setCalendarMonth(
			(month) =>
				new Date(month.getFullYear(), month.getMonth() + monthDelta, 1),
		);
	};

	const closeModal = () => {
		setShowModal(false);
		setEditingAssignment(null);
		setError("");
		setSelectedDates([]);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		if (!editingAssignment && selectedDates.length === 0) {
			setError("Please select at least one assignment date");
			return;
		}

		try {
			if (editingAssignment) {
				await axios.put(`${API_URL}/training/${editingAssignment.id}`, {
					employeeId: parseInt(formData.employeeId),
					departmentId: parseInt(formData.departmentId),
					benchId: formData.benchId
						? parseInt(formData.benchId)
						: null,
					assignmentDate: formData.startDate,
					reason: formData.reason || null,
					trainingRole: formData.trainingRole || null,
				});
				setSuccess("Assignment updated successfully");
			} else {
				await axios.post(`${API_URL}/training/bulk`, {
					employeeId: parseInt(formData.employeeId),
					departmentId: parseInt(formData.departmentId),
					benchId: formData.benchId
						? parseInt(formData.benchId)
						: null,
					dates: [...selectedDates].sort(),
					reason: formData.reason || null,
					trainingRole: formData.trainingRole || null,
				});
				setSuccess(
					`Added ${selectedDates.length} assignment date(s) successfully`,
				);
			}

			closeModal();
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError(
				error.response?.data?.error || "Failed to save assignment",
			);
		}
	};

	const handleDelete = async (id) => {
		try {
			await axios.delete(`${API_URL}/training/${id}`);
			setSuccess("Assignment removed");
			setSelectedIds((prev) => prev.filter((x) => x !== id));
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to remove assignment");
		}
	};

	const handleBulkDelete = async () => {
		if (selectedVisibleIds.length === 0) return;
		try {
			const res = await axios.post(`${API_URL}/training/bulk-delete`, {
				ids: selectedVisibleIds,
			});
			setSuccess(`${res.data.deleted} assignment(s) removed`);
			setSelectedIds([]);
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to remove assignments");
		}
	};

	const toggleSelected = (id) =>
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		);

	const filteredAssignments = assignments.filter((a) => {
		if (!filterMonth) return true;
		return a.assignment_date.startsWith(filterMonth);
	});

	const visibleIds = filteredAssignments.map((a) => a.id);
	const selectedVisibleIds = selectedIds.filter((id) =>
		visibleIds.includes(id),
	);
	const allVisibleSelected =
		visibleIds.length > 0 && selectedVisibleIds.length === visibleIds.length;
	const toggleSelectAll = () =>
		setSelectedIds(allVisibleSelected ? [] : visibleIds);

	const formatDate = (dateStr) => {
		const date = new Date(dateStr + "T00:00:00");
		return date.toLocaleDateString("en-US", {
			weekday: "short",
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getEmployeeName = (employeeId) => {
		const emp = employees.find((e) => e.id === employeeId);
		return emp ? emp.first_name : "Unknown";
	};

	const getDepartmentName = (deptId) => {
		const dept = departments.find((d) => d.id === deptId);
		return dept ? dept.name : "Unknown";
	};

	const getBenchName = (benchId) => {
		const bench = benches.find((b) => b.id === benchId);
		return bench ? bench.name : null;
	};

	const getEmployeeDeptIds = (empId) => {
		const emp = employees.find((e) => e.id === parseInt(empId));
		return (emp?.departments || []).map((d) => d.id);
	};

	// Unless the employee is a trainee, they can only be placed in departments they
	// are already trained in. If the form state breaks that, move to one of their
	// departments (an employee with none can only be a trainee).
	const normalizeDepartment = (data) => {
		if (data.trainingRole === "trainee") return data;
		const allowed = getEmployeeDeptIds(data.employeeId);
		if (allowed.length === 0) return { ...data, trainingRole: "trainee" };
		if (allowed.includes(parseInt(data.departmentId))) return data;
		return { ...data, departmentId: allowed[0], benchId: "" };
	};

	const filteredBenches = benches.filter(
		(b) =>
			!formData.departmentId ||
			b.department_id === parseInt(formData.departmentId),
	);
	const assignmentCalendarDays = getCalendarDays(calendarMonth);
	const selectedEmployee = employees.find(
		(e) => e.id === parseInt(formData.employeeId),
	);
	const selectedEmployeeDeptIds = getEmployeeDeptIds(formData.employeeId);
	const departmentOptions =
		formData.trainingRole === "trainee"
			? departments
			: departments.filter((d) => selectedEmployeeDeptIds.includes(d.id));

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='page-header'>
				<h1 className='page-title'>Forced Assignments</h1>
				<button className='btn btn-primary' onClick={() => openModal()}>
					+ Add Assignment
				</button>
			</div>

			<p className='text-muted mb-md'>
				Use this to assign an employee to work in a specific department
				on certain dates (e.g., for training purposes). If no bench is
				chosen, one is picked automatically.
			</p>

			{success && <div className='alert alert-success'>{success}</div>}
			{error && <div className='alert alert-error'>{error}</div>}

			<div
				className='card'
				style={{ marginBottom: "1rem", padding: "1rem" }}
			>
				<div
					className='form-group'
					style={{
						marginBottom: 0,
						display: "flex",
						alignItems: "flex-end",
						justifyContent: "space-between",
						gap: "1rem",
						flexWrap: "wrap",
					}}
				>
					<div>
						<label className='form-label'>Filter by Month</label>
						<input
							type='month'
							className='form-input'
							value={filterMonth}
							onChange={(e) => setFilterMonth(e.target.value)}
							style={{ maxWidth: "200px" }}
						/>
					</div>
					<button
						className='btn btn-danger'
						onClick={handleBulkDelete}
						disabled={selectedVisibleIds.length === 0}
					>
						Remove Selected
						{selectedVisibleIds.length > 0
							? ` (${selectedVisibleIds.length})`
							: ""}
					</button>
				</div>
			</div>

			<div className='card'>
				<div className='table-container'>
					<table className='table'>
						<thead>
							<tr>
								<th style={{ width: "36px" }}>
									<input
										type='checkbox'
										aria-label='Select all assignments'
										checked={allVisibleSelected}
										onChange={toggleSelectAll}
										disabled={visibleIds.length === 0}
									/>
								</th>
								<th>Employee</th>
								<th>Date</th>
								<th>Department</th>
								<th>Bench</th>
								<th>Reason</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredAssignments.length === 0 ? (
								<tr>
									<td
										colSpan='7'
										className='text-center text-muted'
										style={{ padding: "2rem" }}
									>
										No training assignments found for this
										period.
									</td>
								</tr>
							) : (
								filteredAssignments.map((assignment) => (
									<tr key={assignment.id}>
										<td>
											<input
												type='checkbox'
												aria-label='Select assignment'
												checked={selectedIds.includes(
													assignment.id,
												)}
												onChange={() =>
													toggleSelected(
														assignment.id,
													)
												}
											/>
										</td>
										<td className='font-medium'>
											{getEmployeeName(
												assignment.employee_id,
											)}
											{assignment.training_role ||
											assignment.is_training ? (
												<span
													className='badge badge-warning'
													style={{
														marginLeft: "6px",
													}}
												>
													{assignment.training_role ===
													"trainee"
														? "Trainee"
														: assignment.training_role ===
															  "trainer"
															? "Trainer"
															: "Training"}
												</span>
											) : null}
										</td>
										<td>
											{formatDate(
												assignment.assignment_date,
											)}
										</td>
										<td>
											<span className='badge badge-primary'>
												{getDepartmentName(
													assignment.department_id,
												)}
											</span>
										</td>
										<td>
											{getBenchName(
												assignment.bench_id,
											) || (
												<span className='text-muted'>
													Any
												</span>
											)}
										</td>
										<td>
											{assignment.reason || (
												<span className='text-muted'>
													-
												</span>
											)}
										</td>
										<td>
											<div className='flex gap-sm'>
												<button
													className='btn btn-sm btn-secondary'
													onClick={() =>
														openModal(assignment)
													}
												>
													Edit
												</button>
												<button
													className='btn btn-sm btn-danger'
													onClick={() =>
														handleDelete(
															assignment.id,
														)
													}
												>
													Remove
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Add/Edit Assignment Modal */}
			{showModal && (
				<div className='modal-overlay' onClick={closeModal}>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: editingAssignment ? "500px" : "560px" }}
					>
						<div className='modal-header'>
							<h3>
								{editingAssignment
									? "Edit Assignment"
									: "Add Forced Assignment"}
							</h3>
							<button
								className='modal-close'
								onClick={closeModal}
							>
								&times;
							</button>
						</div>
						<form onSubmit={handleSubmit}>
							<div className='modal-body'>
								{error && (
									<div className='alert alert-error'>
										{error}
									</div>
								)}

								<div className='form-group'>
									<label className='form-label'>
										Purpose *
									</label>
									<select
										className='form-select'
										value={formData.trainingRole || "other"}
										onChange={(e) =>
											setFormData(
												normalizeDepartment({
													...formData,
													trainingRole:
														e.target.value ===
														"other"
															? ""
															: e.target.value,
												}),
											)
										}
									>
										<option value='trainer'>Trainer</option>
										<option value='trainee'>Trainee</option>
										<option value='other'>
											Other reasons
										</option>
									</select>
									<p
										className='text-sm text-muted'
										style={{ marginTop: "0.25rem" }}
									>
										{formData.trainingRole === "trainee"
											? "The trainee can be placed in any department. Add the trainer for the same department and date too — together they cover one staffing slot."
											: formData.trainingRole === "trainer"
												? "A trainer can only work in a department they are already trained in. Add the trainee for the same department and date too — together they cover one staffing slot."
												: "Only departments the employee is already trained in are available."}
									</p>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Employee *
									</label>
									<select
										className='form-select'
										value={formData.employeeId}
										onChange={(e) => {
											const nextEmp = employees.find(
												(emp) =>
													emp.id ===
													parseInt(e.target.value),
											);
											// Drop already-picked dates that are the new employee's NWD
											setSelectedDates((dates) =>
												dates.filter(
													(d) =>
														!nextEmp ||
														!employeeHasRegularDayOff(
															nextEmp,
															new Date(
																d + "T00:00:00",
															),
														),
												),
											);
											setFormData(
												normalizeDepartment({
													...formData,
													employeeId: e.target.value,
												}),
											);
										}}
										required
									>
										{employees.map((emp) => (
											<option key={emp.id} value={emp.id}>
												{emp.first_name}
											</option>
										))}
									</select>
								</div>

								<div className='grid-2'>
									<div className='form-group'>
										<label className='form-label'>
											Department *
										</label>
										<select
											className='form-select'
											value={formData.departmentId}
											onChange={(e) =>
												setFormData({
													...formData,
													departmentId:
														e.target.value,
													benchId: "",
												})
											}
											required
										>
											{departmentOptions.map((dept) => (
												<option
													key={dept.id}
													value={dept.id}
												>
													{dept.name}
												</option>
											))}
										</select>
									</div>
									<div className='form-group'>
										<label className='form-label'>
											Specific Bench
										</label>
										<select
											className='form-select'
											value={formData.benchId}
											onChange={(e) =>
												setFormData({
													...formData,
													benchId: e.target.value,
												})
											}
										>
											<option value=''>Any bench</option>
											{filteredBenches.map((bench) => (
												<option
													key={bench.id}
													value={bench.id}
												>
													{bench.name}
												</option>
											))}
										</select>
									</div>
								</div>

								{editingAssignment ? (
									<div className='form-group'>
										<label className='form-label'>
											Date *
										</label>
										<input
											type='date'
											className='form-input'
											value={formData.startDate}
											onChange={(e) =>
												setFormData({
													...formData,
													startDate: e.target.value,
												})
											}
											required
										/>
									</div>
								) : (
									<div className='form-group'>
										<label className='form-label'>
											Select Assignment Dates *
										</label>
										<p
											className='text-sm text-muted'
											style={{ marginBottom: "0.5rem" }}
										>
											Click dates to select or deselect
											them.
										</p>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												marginBottom: "0.5rem",
											}}
										>
											<button
												type='button'
												className='btn btn-sm btn-secondary'
												onClick={() =>
													changeCalendarMonth(-1)
												}
											>
												◀
											</button>
											<span className='font-medium'>
												{calendarMonth.toLocaleDateString(
													"en-US",
													{
														month: "long",
														year: "numeric",
													},
												)}
											</span>
											<button
												type='button'
												className='btn btn-sm btn-secondary'
												onClick={() =>
													changeCalendarMonth(1)
												}
											>
												▶
											</button>
										</div>
										<div
											style={{
												display: "grid",
												gridTemplateColumns:
													"repeat(7, 1fr)",
												gap: "2px",
												background: "var(--gray-200)",
												border: "1px solid var(--gray-200)",
												borderRadius: "var(--radius-md)",
												overflow: "hidden",
											}}
										>
											{[
												"Sun",
												"Mon",
												"Tue",
												"Wed",
												"Thu",
												"Fri",
												"Sat",
											].map((day) => (
												<div
													key={day}
													style={{
														padding: "0.5rem",
														textAlign: "center",
														fontWeight: 600,
														fontSize: "0.75rem",
														background:
															"var(--gray-100)",
													}}
												>
													{day}
												</div>
											))}
											{assignmentCalendarDays.map(
												(day, idx) => {
													if (!day)
														return (
															<div
																key={`empty-${idx}`}
																style={{
																	background:
																		"white",
																}}
															/>
														);
													const dateStr =
														formatDateStr(day);
													const isSelected =
														selectedDates.includes(
															dateStr,
														);
													const isPast =
														day <
														new Date(
															new Date().setHours(
																0,
																0,
																0,
																0,
															),
														);
													const isNwd =
														selectedEmployee &&
														employeeHasRegularDayOff(
															selectedEmployee,
															day,
														);
													const isBlocked =
														isPast || isNwd;
													return (
														<button
															type='button'
															key={dateStr}
															disabled={isBlocked}
															title={
																isNwd
																	? "Regular day off (NWD)"
																	: undefined
															}
															onClick={() =>
																toggleAssignmentDate(
																	dateStr,
																)
															}
															style={{
																border: 0,
																padding: "0.5rem",
																textAlign:
																	"center",
																background:
																	isSelected
																		? "var(--primary)"
																		: isNwd
																			? "#e3f2fd"
																			: "white",
																color: isSelected
																	? "white"
																	: isBlocked
																		? "var(--gray-400)"
																		: "inherit",
																cursor: isBlocked
																	? "not-allowed"
																	: "pointer",
																fontWeight:
																	isSelected
																		? 600
																		: "normal",
															}}
														>
															{day.getDate()}
															{isNwd && (
																<div
																	style={{
																		fontSize:
																			"0.55rem",
																		lineHeight: 1,
																	}}
																>
																	NWD
																</div>
															)}
														</button>
													);
												},
											)}
										</div>
										{selectedDates.length > 0 && (
											<div
												style={{ marginTop: "0.75rem" }}
											>
												<span className='text-sm font-medium'>
													Selected:{" "}
													{selectedDates.length}{" "}
													day(s)
												</span>
												<div
													style={{
														display: "flex",
														flexWrap: "wrap",
														gap: "0.25rem",
														marginTop: "0.25rem",
													}}
												>
													{selectedDates.map(
														(date) => (
															<button
																type='button'
																key={date}
																className='badge badge-primary'
																style={{
																	border: 0,
																	cursor: "pointer",
																}}
																onClick={() =>
																	toggleAssignmentDate(
																		date,
																	)
																}
															>
																{new Date(
																	date +
																		"T00:00:00",
																).toLocaleDateString(
																	"en-US",
																	{
																		month: "short",
																		day: "numeric",
																	},
																)}{" "}
																×
															</button>
														),
													)}
												</div>
											</div>
										)}
									</div>
								)}

								<div className='form-group'>
									<label className='form-label'>
										Notes (optional)
									</label>
									<input
										type='text'
										className='form-input'
										value={formData.reason}
										onChange={(e) =>
											setFormData({
												...formData,
												reason: e.target.value,
											})
										}
										placeholder='e.g., Cross-training, coverage request'
									/>
								</div>
							</div>
							<div className='modal-footer'>
								<button
									type='button'
									className='btn btn-secondary'
									onClick={closeModal}
								>
									Cancel
								</button>
								<button
									type='submit'
									className='btn btn-primary'
									disabled={
										!editingAssignment &&
										selectedDates.length === 0
									}
								>
									{editingAssignment
										? "Update Assignment"
										: `Add Assignment (${selectedDates.length} date${selectedDates.length === 1 ? "" : "s"})`}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default TrainingAssignments;
